/**
 * Result of parsing a CSV file for repository URLs.
 */
export type CSVParseResult = {
    /** Valid repository URLs that were successfully parsed */
    validUrls: string[];
    /** Count of lines that were skipped (invalid format, missing host, etc.) */
    skippedCount: number;
    /** Total non-empty, non-header lines processed */
    totalProcessed: number;
};

/**
 * Result of parsing a CSV file into structured data.
 */
export type CSVData = {
    /** Column headers (either from first row or auto-generated) */
    headers: string[];
    /** Data rows (excluding header if present) */
    rows: string[][];
    /** Whether headers were detected/provided */
    hasHeaders: boolean;
};

/**
 * Options for parsing CSV content.
 */
export type CSVParseOptions = {
    /** Column separator character (default: ",") */
    separator?: string;
    /** Whether the first row contains headers (default: auto-detect) */
    hasHeaders?: boolean;
};

/**
 * Parses a single CSV line into fields, handling quoted values.
 */
function parseCSVLine(line: string, separator: string): string[] {
    const fields: string[] = [];
    let current = "";
    let inQuotes = false;
    let i = 0;

    while (i < line.length) {
        const char = line[i];

        if (inQuotes) {
            if (char === '"') {
                if (i + 1 < line.length && line[i + 1] === '"') {
                    // Escaped quote
                    current += '"';
                    i += 2;
                } else {
                    // End of quoted field
                    inQuotes = false;
                    i++;
                }
            } else {
                current += char;
                i++;
            }
        } else {
            if (char === '"') {
                inQuotes = true;
                i++;
            } else if (char === separator) {
                fields.push(current.trim());
                current = "";
                i++;
            } else {
                current += char;
                i++;
            }
        }
    }

    fields.push(current.trim());
    return fields;
}

/**
 * Auto-detects if the first row looks like a header row.
 * A row is considered a header if it contains common header words as standalone terms
 * AND does not look like a URL or path.
 */
function detectHeaders(firstRow: string[]): boolean {
    // Header words that indicate a header row (must be standalone or at word boundaries)
    const headerWords = ["repository", "repo", "url", "name", "project", "link", "path", "id", "column", "description"];

    // If any cell looks like a URL or path, it's probably not a header row
    const hasUrlLikeCell = firstRow.some((cell) => {
        const trimmed = cell.trim().toLowerCase();
        return (
            trimmed.startsWith("http://") ||
            trimmed.startsWith("https://") ||
            trimmed.includes(".com/") ||
            trimmed.includes(".org/") ||
            trimmed.includes(".io/") ||
            // Check for path-like patterns (e.g., owner/repo)
            /^[a-z0-9_-]+\/[a-z0-9_-]+$/i.test(trimmed)
        );
    });

    if (hasUrlLikeCell) {
        return false;
    }

    // Check if any cell matches a header word (case-insensitive, as whole word or with common suffixes)
    return firstRow.some((cell) => {
        const lower = cell.toLowerCase().trim();
        return headerWords.some((word) => lower === word || lower.startsWith(word + "_") || lower.endsWith("_" + word));
    });
}

/**
 * Parses CSV content into structured data with columns.
 */
export function parseCSVData(content: string, options: CSVParseOptions = {}): CSVData {
    const separator = options.separator ?? ",";
    const lines = content.split(/\r?\n/).filter((line) => line.trim() !== "");

    if (lines.length === 0) {
        return { headers: [], rows: [], hasHeaders: false };
    }

    const allRows = lines.map((line) => parseCSVLine(line, separator));

    // Determine column count from the row with most columns
    const columnCount = Math.max(...allRows.map((row) => row.length));

    // Normalize all rows to have the same number of columns
    const normalizedRows = allRows.map((row) => {
        while (row.length < columnCount) {
            row.push("");
        }
        return row;
    });

    // Determine if first row is headers
    const hasHeaders = options.hasHeaders ?? detectHeaders(normalizedRows[0]);

    let headers: string[];
    let dataRows: string[][];

    if (hasHeaders && normalizedRows.length > 0) {
        headers = normalizedRows[0];
        dataRows = normalizedRows.slice(1);
    } else {
        // Generate column names: Column 1, Column 2, etc.
        headers = Array.from({ length: columnCount }, (_, i) => `Column ${i + 1}`);
        dataRows = normalizedRows;
    }

    return { headers, rows: dataRows, hasHeaders };
}

/**
 * Extracts repository URLs from a specific column of parsed CSV data.
 */
export function extractUrlsFromColumn(data: CSVData, columnIndex: number): CSVParseResult {
    const validUrls: string[] = [];
    let skippedCount = 0;

    for (const row of data.rows) {
        const value = row[columnIndex]?.trim() ?? "";
        if (value && isValidRepositoryInput(value)) {
            validUrls.push(value);
        } else if (value) {
            skippedCount++;
        }
    }

    return {
        validUrls,
        skippedCount,
        totalProcessed: data.rows.length,
    };
}

/**
 * Parses CSV content and extracts repository URLs from the first column.
 * Supports both simple lists (one URL per line) and proper CSV format.
 *
 * @param content - The CSV file content as a string
 * @returns Parse result with valid URLs and skip statistics
 */
export function parseRepositoryCSV(content: string): CSVParseResult {
    const data = parseCSVData(content);
    if (data.headers.length === 0) {
        return { validUrls: [], skippedCount: 0, totalProcessed: 0 };
    }
    return extractUrlsFromColumn(data, 0);
}

/**
 * Validates if a string looks like a valid repository URL.
 * Requires full URLs with host information:
 * - Full URLs: https://github.com/owner/repo
 * - Context URLs with host: github.com/owner/repo
 *
 * Short form (owner/repo) is NOT accepted as it lacks host information
 * needed to determine which SCM provider to use.
 */
export function isValidRepositoryInput(input: string): boolean {
    const trimmed = input.trim();
    if (!trimmed) return false;

    // Full URL pattern (https:// or http://)
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
        // Must have at least host/owner/repo structure
        const urlParts = trimmed.replace(/^https?:\/\//, "").split("/");
        return urlParts.length >= 3 && urlParts.every((p) => p.length > 0);
    }

    // Context URL with host: host/owner/repo (e.g., github.com/owner/repo)
    const parts = trimmed.split("/");
    if (parts.length >= 3 && parts.every((p) => p.length > 0)) {
        // First part should look like a hostname (contains a dot)
        if (parts[0].includes(".")) {
            return true;
        }
    }

    return false;
}

/**
 * Normalizes repository input to a consistent format.
 * Removes trailing .git suffix and normalizes whitespace.
 */
export function normalizeRepositoryUrl(input: string): string {
    let url = input.trim();

    // Remove trailing slash first
    while (url.endsWith("/")) {
        url = url.slice(0, -1);
    }

    // Remove trailing .git
    if (url.endsWith(".git")) {
        url = url.slice(0, -4);
    }

    // Remove any trailing slashes that might have been before .git
    while (url.endsWith("/")) {
        url = url.slice(0, -1);
    }

    return url;
}

/**
 * Deduplicates repository URLs while preserving order.
 */
export function deduplicateUrls(urls: string[]): string[] {
    const seen = new Set<string>();
    const result: string[] = [];

    for (const url of urls) {
        const normalized = normalizeRepositoryUrl(url).toLowerCase();
        if (!seen.has(normalized)) {
            seen.add(normalized);
            result.push(normalizeRepositoryUrl(url));
        }
    }

    return result;
}

/**
 * Detects which column in CSV data most likely contains repository URLs.
 * Returns the column index with the highest score, or 0 if no good match found.
 */
export function detectRepositoryColumn(data: CSVData): number {
    if (data.headers.length === 0) return 0;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const scores: number[] = new Array(data.headers.length).fill(0);

    // Score based on header names
    const repoHeaderPatterns = [
        /repo(sitory)?/i,
        /url/i,
        /github/i,
        /gitlab/i,
        /bitbucket/i,
        /clone/i,
        /source/i,
        /link/i,
    ];

    for (let i = 0; i < data.headers.length; i++) {
        const header = data.headers[i].toLowerCase();
        for (const pattern of repoHeaderPatterns) {
            if (pattern.test(header)) {
                scores[i] += 10;
            }
        }
    }

    // Score based on content - sample first few rows
    const sampleRows = data.rows.slice(0, Math.min(10, data.rows.length));
    for (const row of sampleRows) {
        for (let i = 0; i < row.length; i++) {
            const value = row[i]?.trim() ?? "";
            if (isValidRepositoryInput(value)) {
                scores[i] += 5;
            } else if (
                value.includes("github.com") ||
                value.includes("gitlab.com") ||
                value.includes("bitbucket.org")
            ) {
                scores[i] += 3;
            } else if (value.includes("/") && value.split("/").length >= 2) {
                // Looks like it might be owner/repo format
                scores[i] += 1;
            }
        }
    }

    // Find the column with the highest score
    let bestColumn = 0;
    let bestScore = scores[0];
    for (let i = 1; i < scores.length; i++) {
        if (scores[i] > bestScore) {
            bestScore = scores[i];
            bestColumn = i;
        }
    }

    return bestColumn;
}
